import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Lock, FileEdit } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { cn, formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { DynamicFormRenderer, type CampoExploracionDefinicion } from '@/components/formularios-dinamicos/DynamicFormRenderer';

interface PlantillaExploracion {
  id: string;
  nombre: string;
  esquemaJson: CampoExploracionDefinicion[];
  isActive: boolean;
}

interface ExploracionDinamica {
  id: string;
  pacienteId: string;
  plantillaId: string;
  datosJson: Record<string, unknown>;
  firmado: boolean;
  createdAt: string;
  updatedAt: string;
  plantilla?: PlantillaExploracion;
}

type ActiveRecord = {
  id: string | null; // null = captura nueva, aún no guardada
  plantilla: PlantillaExploracion;
  datosJson: Record<string, unknown>;
  firmado: boolean;
};

interface Props {
  id: string;
  canEdit: boolean;
  /** Si se captura desde una consulta activa, vincula la exploración nueva a esa consulta. */
  consultaId?: string;
}

export function ExploracionesDinamicasTab({ id: pacienteId, canEdit, consultaId }: Props) {
  const qc = useQueryClient();
  const [activeRecord, setActiveRecord] = useState<ActiveRecord | null>(null);
  const [plantillaParaNueva, setPlantillaParaNueva] = useState('');

  const { data: plantillas = [], isLoading: loadingPlantillas } = useQuery<PlantillaExploracion[]>({
    queryKey: ['plantillas-exploracion-activas'],
    queryFn: () => api.get('/api/formularios-dinamicos/plantillas', { params: { soloActivas: true } }).then((r) => r.data),
    enabled: canEdit,
  });

  const { data: historial = [], isLoading: loadingHistorial, isError: errorHistorial } = useQuery<ExploracionDinamica[]>({
    queryKey: ['exploraciones-dinamicas', pacienteId],
    queryFn: () => api.get(`/api/pacientes/${pacienteId}/exploraciones-dinamicas`).then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (dto: { plantillaId: string; datosJson: Record<string, unknown>; firmar?: boolean }) =>
      api.post(`/api/pacientes/${pacienteId}/exploraciones-dinamicas`, { ...dto, consultaId: consultaId ?? null }).then((r) => r.data),
  });

  const updateMutation = useMutation({
    mutationFn: ({ exploracionId, datosJson, firmar }: { exploracionId: string; datosJson: Record<string, unknown>; firmar?: boolean }) =>
      api.patch(`/api/pacientes/${pacienteId}/exploraciones-dinamicas/${exploracionId}`, { datosJson, firmar }).then((r) => r.data),
  });

  const guardando = createMutation.isPending || updateMutation.isPending;

  function iniciarNueva() {
    const plantilla = plantillas.find((p) => p.id === plantillaParaNueva);
    if (!plantilla) return;
    setActiveRecord({ id: null, plantilla, datosJson: {}, firmado: false });
  }

  function continuarBorrador(exp: ExploracionDinamica) {
    if (!exp.plantilla) { toast.error('La plantilla usada en este borrador ya no existe'); return; }
    setActiveRecord({ id: exp.id, plantilla: exp.plantilla, datosJson: exp.datosJson, firmado: false });
  }

  function verFirmada(exp: ExploracionDinamica) {
    if (!exp.plantilla) { toast.error('La plantilla usada en esta exploración ya no existe'); return; }
    setActiveRecord({ id: exp.id, plantilla: exp.plantilla, datosJson: exp.datosJson, firmado: true });
  }

  function cerrarCaptura() {
    setActiveRecord(null);
    setPlantillaParaNueva('');
  }

  async function guardar(values: Record<string, unknown>, firmar: boolean) {
    if (!activeRecord) return;
    try {
      if (activeRecord.id) {
        await updateMutation.mutateAsync({ exploracionId: activeRecord.id, datosJson: values, firmar });
      } else {
        await createMutation.mutateAsync({ plantillaId: activeRecord.plantilla.id, datosJson: values, firmar });
      }
      qc.invalidateQueries({ queryKey: ['exploraciones-dinamicas', pacienteId] });
      toast.success(firmar ? 'Exploración guardada y firmada' : 'Borrador guardado');
      cerrarCaptura();
    } catch (err) {
      toast.error(getErrorMessage(err, firmar ? 'Error al firmar la exploración' : 'Error al guardar el borrador'));
    }
  }

  return (
    <div className="space-y-6">
      {/* Captura: nueva exploración o continuar un borrador */}
      {canEdit && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Exploración dinámica</p>

          {!activeRecord && (
            <div className="flex items-center gap-2">
              <select
                value={plantillaParaNueva}
                onChange={(e) => setPlantillaParaNueva(e.target.value)}
                disabled={loadingPlantillas}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="">Selecciona una plantilla de exploración...</option>
                {plantillas.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={!plantillaParaNueva}
                onClick={iniciarNueva}
                className="flex items-center gap-1.5 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex-shrink-0"
              >
                <Plus size={13} /> Iniciar
              </button>
            </div>
          )}

          {!loadingPlantillas && plantillas.length === 0 && !activeRecord && (
            <p className="text-xs text-text-secondary">
              No hay plantillas de exploración activas. Un administrador puede crearlas en Configuración › Exploraciones dinámicas.
            </p>
          )}

          {activeRecord && !activeRecord.firmado && (
            <div className="space-y-3 border border-primary-200 rounded-xl bg-primary-50/40 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-text-primary">{activeRecord.plantilla.nombre}</p>
                <button type="button" onClick={cerrarCaptura} className="text-xs text-text-secondary hover:text-text-primary flex items-center gap-1">
                  <X size={12} /> Cancelar
                </button>
              </div>
              <DynamicFormRenderer
                esquemaJson={activeRecord.plantilla.esquemaJson}
                initialValues={activeRecord.datosJson}
                actions={[
                  {
                    key: 'draft',
                    label: guardando ? 'Guardando...' : 'Guardar Borrador',
                    variant: 'secondary',
                    disabled: guardando,
                    requiresValidation: false,
                    onClick: (values) => guardar(values, false),
                  },
                  {
                    key: 'firmar',
                    label: 'Guardar y Firmar',
                    variant: 'primary',
                    disabled: guardando,
                    onClick: (values) => guardar(values, true),
                  },
                ]}
              />
              <p className="text-[11px] text-text-secondary">
                "Guardar Borrador" conserva lo capturado aunque falten campos. "Guardar y Firmar" exige completar los campos requeridos y
                bloquea la exploración de forma permanente (NOM-004-SSA3-2012).
              </p>
            </div>
          )}
        </div>
      )}

      {/* Vista de solo lectura de una exploración firmada seleccionada en el historial */}
      {activeRecord && activeRecord.firmado && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-text-primary">{activeRecord.plantilla.nombre}</p>
              <span className="flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                <Lock size={9} /> Firmada
              </span>
            </div>
            <button type="button" onClick={cerrarCaptura} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
              <X size={14} />
            </button>
          </div>
          <DynamicFormRenderer esquemaJson={activeRecord.plantilla.esquemaJson} initialValues={activeRecord.datosJson} disabled />
        </div>
      )}

      {/* Historial */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Historial de exploraciones</p>

        {loadingHistorial && <p className="text-sm text-text-secondary text-center py-6">Cargando...</p>}
        {errorHistorial && <p className="text-sm text-red-500 text-center py-6">Error al cargar el historial</p>}

        {!loadingHistorial && !errorHistorial && historial.length === 0 && (
          <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <FileEdit size={24} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">Sin exploraciones registradas</p>
          </div>
        )}

        {!loadingHistorial && historial.length > 0 && (
          <div className="space-y-2">
            {historial.map((exp) => (
              <button
                key={exp.id}
                type="button"
                onClick={() => (exp.firmado ? verFirmada(exp) : canEdit ? continuarBorrador(exp) : verFirmada(exp))}
                className="w-full text-left flex items-center justify-between px-4 py-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{exp.plantilla?.nombre ?? 'Plantilla eliminada'}</p>
                  <p className="text-xs text-text-secondary mt-0.5">{formatDate(exp.createdAt)}</p>
                </div>
                <span
                  className={cn(
                    'text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ml-3 flex items-center gap-1',
                    exp.firmado ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200',
                  )}
                >
                  {exp.firmado ? (<><Lock size={9} /> Firmada</>) : 'Borrador'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
